import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Users, Plus, Edit, Trash2 } from 'lucide-react'
import { adminAPI } from '../../services/api'
import type { User } from '../../types'
import toast from 'react-hot-toast'

const UserManagement: React.FC = () => {
  const { t } = useTranslation()
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    planId: '6'
  })

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsers()
      console.log('Admin users response:', response.data)
      
      // Handle different response formats
      const responseData = response.data as any
      if (responseData && responseData.success && responseData.data) {
        // API zwraca {success: true, data: [...]}
        setUsers(responseData.data)
      } else if (Array.isArray(responseData)) {
        setUsers(responseData)
      } else if (responseData && responseData.users) {
        setUsers(responseData.users)
      } else {
        console.error('Unexpected users response format:', responseData)
        setUsers([])
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      toast.error('Błąd pobierania użytkowników')
      setUsers([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Wypełnij wszystkie pola')
      return
    }

    try {
      const userData: any = {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role
      }
      
      // Jeśli wybrano plan, dodaj go do danych
      if (newUser.planId) {
        userData.plan_id = parseInt(newUser.planId)
        userData.duration_days = 30
      }
      
      const response = await adminAPI.createUser(userData)
      
      if (response.data.success) {
        toast.success('Użytkownik został utworzony pomyślnie')
        if (response.data.license_key) {
          toast.success(`Licencja: ${response.data.license_key}`)
        }
      }
      
      setShowCreateForm(false)
      setNewUser({ name: '', email: '', password: '', role: 'user', planId: '6' })
      fetchUsers()
    } catch (error: any) {
      console.error('Create user error:', error)
      toast.error(error.response?.data?.message || 'Błąd tworzenia użytkownika')
    }
  }

  const handleEditUser = async () => {
    if (!editingUser) return

    try {
      await adminAPI.updateUser(editingUser.id, {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role
      })
      toast.success('Użytkownik został zaktualizowany')
      setShowEditForm(false)
      setEditingUser(null)
      fetchUsers()
    } catch (error) {
      toast.error('Błąd aktualizacji użytkownika')
    }
  }

  const handleDeleteUser = async (userId: number) => {
    if (!confirm('Czy na pewno chcesz usunąć tego użytkownika?')) return

    try {
      await adminAPI.deleteUser(userId)
      toast.success('Użytkownik został usunięty')
      fetchUsers()
    } catch (error) {
      toast.error('Błąd usuwania użytkownika')
    }
  }

  const startEdit = (user: User) => {
    setEditingUser({...user})
    setShowEditForm(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('admin.users')}</h1>
          <p className="text-gray-400">Zarządzaj kontami użytkowników</p>
        </div>
        <button 
          onClick={() => setShowCreateForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">
          <Plus className="h-4 w-4" />
          <span>{t('admin.createUser')}</span>
        </button>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <h3 className="text-lg font-semibold text-white mb-4">Utwórz nowego użytkownika</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Imię i nazwisko</label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
                placeholder="Jan Kowalski"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Hasło</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
                placeholder="Bezpieczne hasło"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Rola</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
              >
                <option value="user">Użytkownik</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">Licencja startowa</label>
              <select
                value={newUser.planId}
                onChange={(e) => setNewUser(prev => ({ ...prev, planId: e.target.value }))}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
              >
                <option value="">Bez licencji</option>
                <option value="6">Standard - 5 urządzeń (24.99 EUR/rok)</option>
                <option value="7">Pro - 50 urządzeń (59.99 EUR/rok)</option>
                <option value="8">Unlimited - bez limitu urządzeń (599.00 EUR - dożywotnio)</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">Opcjonalnie - wybierz plan aby od razu przypisać licencję użytkownikowi</p>
            </div>
          </div>
          <div className="mt-4 flex space-x-2">
            <button
              onClick={handleCreateUser}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
            >
              Utwórz użytkownika
            </button>
            <button
              onClick={() => {
                setShowCreateForm(false)
                setNewUser({ name: '', email: '', password: '', role: 'user', planId: '6' })
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500"
            >
              Anuluj
            </button>
          </div>
        </div>
      )}

      {/* Edit User Form */}
      {showEditForm && editingUser && (
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <h3 className="text-lg font-semibold text-white mb-4">Edytuj użytkownika</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Imię i nazwisko</label>
              <input
                type="text"
                value={editingUser.name}
                onChange={(e) => setEditingUser(prev => prev ? {...prev, name: e.target.value} : null)}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
              <input
                type="email"
                value={editingUser.email}
                onChange={(e) => setEditingUser(prev => prev ? {...prev, email: e.target.value} : null)}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Rola</label>
              <select
                value={editingUser.role}
                onChange={(e) => setEditingUser(prev => prev ? {...prev, role: e.target.value} : null)}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-white"
              >
                <option value="user">Użytkownik</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex space-x-2">
            <button
              onClick={handleEditUser}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
            >
              Zapisz zmiany
            </button>
            <button
              onClick={() => {
                setShowEditForm(false)
                setEditingUser(null)
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500"
            >
              Anuluj
            </button>
          </div>
        </div>
      )}

      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Users className="h-5 w-5 text-primary mr-2" />
          Lista użytkowników
        </h2>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse bg-dark-700 h-16 rounded"></div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <p className="text-center text-gray-400 py-8">Brak użytkowników do wyświetlenia</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Użytkownik</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Rola</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Data rejestracji</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-dark-700 hover:bg-dark-700/50">
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium text-white">{user.name}</p>
                        <p className="text-sm text-gray-400">ID: {user.id}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-300">{user.email}</p>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
                        user.role === 'admin' 
                          ? 'bg-red-900/20 text-red-400 border-red-900/50'
                          : 'bg-blue-900/20 text-blue-400 border-blue-900/50'
                      }`}>
                        {user.role === 'admin' ? 'Administrator' : 'Użytkownik'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-300">
                        {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => startEdit(user)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-600/10 rounded-lg transition-colors">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-600/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default UserManagement